import { Injectable } from "@angular/core";

import {
  FormGroup,
  FormBuilder,
  Validators,
  ValidationErrors,
  FormArray,
} from "@angular/forms";
import { CookiesLocalService } from "../cookies-local.service";
import { CookieService } from "ngx-cookie-service";
import { Configuration } from "src/app/configuration";
import { ApplicantService } from "../applicant.service";
import { IApplicantExamination } from "src/app/model/IApplicantExamination";
import { IApplicantExaminationItem } from "src/app/model/IApplicantExaminationItem";

@Injectable({
  providedIn: "root",
})
export class FormDataService {
  private formData: FormData = new FormData();

  constructor(
    private fb: FormBuilder,
    private cookieLocal: CookiesLocalService,
    private cookieService: CookieService,
    private applicantService: ApplicantService
  ) {}

  getPersonalBio(data): FormGroup {
    return this.fb.group({
      FirstName: [data.FirstName, Validators.required],
      LastName: [data.LastName, Validators.required],
      OtherName: [data.OtherName],
      MarritalStatusId: [data.MarritalStatusId, Validators.required],
      GenderId: [data.GenderId, Validators.required],
      TitleId: [data.TitleId, Validators.required],
      DateOfBirth: [data.DateOfBirthDisplay, Validators.required],
      EthnicGroupId: [data.EthnicGroupId, Validators.required],
      DisabilityId: [data.DisabilityId, Validators.required],
      PlaceOfBirth: [data.PlaceOfBirth, Validators.required],
      ReligionId: [data.ReligionId, Validators.required],
    });
  }

  getGuardianInfo(data): FormGroup {
    return this.fb.group({
      GuardianId: [data.GuardianId, Validators.required],
      Name: [data.Name, Validators.required],
      Email: [data.Email],
      PrimaryNumber: [data.PrimaryNumber, Validators.required],
      OtherNumber: [data.OtherNumber],
      City: [data.City, Validators.required],
      ResidentialAddress1: [data.ResidentialAddress1, Validators.required],
      AddressLine1: [data.AddressLine1, Validators.required],
      RelationshipToStudentId: [
        data.RelationshipToStudentId,
        Validators.required,
      ],
      OccupationId: [data.OccupationId, Validators.required],
      // IsUniversityStaff:[false,Validators.required],
      RegionId: [data.RegionId],
      FaxNumber: [data.FaxNumber],
      GhanaPostCode: [""],
      StaffPosition: [data.StaffPosition],
    });
  }

  setGuardianInfo(data) {
    let model = this.cookieLocal.getGuardianData();
    model.GuardianId = data.GuardianId;
    model.Name = data.Name;
    model.Email = data.Email;
    model.PrimaryNumber = data.PrimaryNumber;
    model.OtherNumber = data.OtherNumber;
    model.City = data.City;
    model.ResidentialAddress1 = data.ResidentialAddress1;
    model.AddressLine1 = data.AddressLine1;
    model.RelationshipToStudentId = data.RelationshipToStudentId;
    model.OccupationId = data.OccupationId;
    model.RegionId = data.RegionId;
    model.FaxNumber = data.FaxNumber;
    model.StaffPosition = data.StaffPosition;

    this.cookieLocal.getRefreshedGuardianData(model);
    this.applicantService.saveGuardianData(model).subscribe(
      (data) => {},
      (error) => {
        console.log(error);
      }
    );
  }

  addExaminationFormGroup(data: IApplicantExamination = null): FormGroup {
    let moreItems = [];

    if (data) {
      return this.fb.group({
        ApplicantExaminationId: [data.ApplicantExaminationId],
        ExaminationCentre: [data.ExaminationCentre, Validators.required],
        YearOfExamSitting: [data.YearOfExamSitting, Validators.required],
        ExamIndexNumber: [data.ExamIndexNumber, Validators.required],
        SchoolCode: [data.SchoolCode, Validators.required],
        ExamPeriodId: [data.ExamPeriodId, Validators.required],
        RegionId: [data.RegionId, Validators.required],
        ApplicationModeId: [data.ApplicationModeId, Validators.required],
        InstitutionId: [data.InstitutionId],
        InstitutionName: [data.InstitutionName],
        ApplicantExaminationTypeId: [
          data.ApplicantExaminationTypeId,
          Validators.required,
        ],
        ApplicantExaminationItems: this.buildExaminationItemFormGroup(
          data.ApplicantExaminationItems
        ),
      });
    }

    return this.fb.group({
      ApplicantExaminationId: [""],
      ExaminationCentre: ["", Validators.required],
      YearOfExamSitting: ["", Validators.required],
      ExamIndexNumber: ["", Validators.required],
      SchoolCode: ["", Validators.required],
      ExamPeriodId: ["", Validators.required],
      RegionId: ["", Validators.required],
      ApplicationModeId: ["", Validators.required],
      InstitutionId: ["", Validators.required],
      ApplicantExaminationTypeId: ["", Validators.required],
      InstitutionName: [""],
      ApplicantExaminationItems: this.fb.array([]),
    });
  }

  buildExaminationItemFormGroup(
    applicantExaminationItems: IApplicantExaminationItem[]
  ) {
    let arr = new FormArray([]);
    applicantExaminationItems.forEach((item) => {
      arr.push(this.addExaminationItemFormGroup(item));
    });
    return arr;
  }

  addExaminationItemFormGroup(
    applicantExaminationItem: IApplicantExaminationItem
  ) {
    return this.fb.group({
      ApplicantExaminationItemId: [
        applicantExaminationItem.ApplicantExaminationItemId,
      ],
      ApplicantExaminationId: [
        applicantExaminationItem.ApplicantExaminationId,
        Validators.required,
      ],
      ApplicantCourseId: [
        applicantExaminationItem.ApplicantCourseId,
        Validators.required,
      ],
      ApplicantCourseGradeTypeItemId: [
        applicantExaminationItem.ApplicantCourseGradeTypeItemId,
        Validators.required,
      ],
      ApplicantCourseName: [
        applicantExaminationItem.ApplicantCourseName,
        Validators.required,
      ],
    });
  }

  setBasicInfo(data) {
    let model = this.cookieLocal.getApplicantData();
    model.FirstName = data.FirstName;
    model.LastName = data.LastName;
    model.OtherName = data.OtherName;
    model.MarritalStatusId = data.MarritalStatusId;
    model.GenderId = data.GenderId;
    model.TitleId = data.TitleId;
    model.DateOfBirth = data.DateOfBirth;
    model.EthnicGroupId = data.EthnicGroupId;
    model.DisabilityId = data.DisabilityId;
    model.PlaceOfBirth = data.PlaceOfBirth;
    model.ReligionId = data.ReligionId;
    this.cookieLocal.getRefreshedApplicantData(model);
    this.applicantService.savePersonalData(model).subscribe(
      (data) => {},
      (error) => {
        console.log(error);
      }
    );
  }

  setContactInfo(data) {
    let model = this.cookieLocal.getApplicantData();
    model.CountryId = data.CountryId;
    model.City = data.City;
    model.Email = data.Email;
    model.PrimaryNumber = data.PrimaryNumber;
    model.OtherNumber = data.OtherNumber;
    model.ResidentialAddress1 = data.ResidentialAddress1;
    model.AddressLine1 = data.AddressLine1;
    model.RegionId = data.RegionId;
    model.FaxNumber = data.FaxNumber;
    model.GhanaPostCode = data.GhanaPostCode;
    this.cookieService.set(
      Configuration.localStorageData.applicaintData,
      JSON.stringify(model)
    );
    this.applicantService.savePersonalData(model).subscribe(
      (data) => {},
      (error) => {
        console.log(error);
      }
    );
  }

  getFormData(): FormData {
    // Return the entire Form Data
    return this.formData;
  }

  isFormValid() {
    // Return true if all forms had been validated successfully; otherwise, return false
    // return this.getPersonalContact().valid &&
    //         this.getPersonalContact().valid;
    return true;
  }
}
